import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const NAV = [
  { label: "About", to: "/about" },
  { label: "Team", to: "/team" },
  { label: "What we do", to: "/what-we-do" },
  { label: "Products", to: "/products" },
  { label: "Solutions", to: "/solutions" },
  { label: "Industries", to: "/industries" },
  { label: "Methodology", to: "/methodology" },
  { label: "Why OpsIQ", to: "/why-opsiq" },
];

export default function OpsHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="ops-header"
      className={`sticky top-0 z-50 backdrop-blur-xl transition-all ${
        scrolled ? "bg-[#1A2E4A]/90 border-b border-white/10" : "bg-[#1A2E4A]/60 border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 md:h-24 flex items-center justify-between gap-6">
        <Link to="/" data-testid="ops-header-logo" className="flex items-center gap-3 group">
          <img
            src="/opsiq-logo-primary.png"
            alt="OpsIQ"
            className="h-9 md:h-11 w-auto object-contain"
            style={{ filter: "brightness(0) invert(1) opacity(0.96)" }}
          />
          <motion.span
            animate={{ opacity: [1, 0.3, 1] }}
            transition={{ duration: 1.6, repeat: Infinity }}
            className="hidden md:inline-block h-1.5 w-1.5 rounded-full bg-[#D9FF3C]"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {NAV.map((n) => {
            const active = loc.pathname === n.to;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`text-[11px] uppercase tracking-[0.22em] font-medium transition-colors ${
                  active ? "text-[#D9FF3C]" : "text-[#F1ECE0]/70 hover:text-[#F1ECE0]"
                }`}
              >
                {n.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-contact"
            className="text-[11px] uppercase tracking-[0.22em] font-medium text-[#F1ECE0]/70 hover:text-[#F1ECE0] transition-colors"
          >
            Contact
          </Link>
          <Link
            to="/book"
            data-testid="header-book-btn"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold px-5 py-3 bg-[#D9FF3C] text-[#142645] hover:bg-[#E4FF66] transition-colors"
          >
            Book consultation
          </Link>
        </div>

        <button
          data-testid="ops-mobile-toggle"
          className="lg:hidden p-2 text-[#F1ECE0]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            data-testid="ops-mobile-menu"
            className="lg:hidden border-t border-white/10 bg-[#142645]"
          >
            <div className="px-6 py-6 flex flex-col gap-3">
              {NAV.concat([{ label: "Contact", to: "/contact" }]).map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className="text-sm uppercase tracking-[0.22em] font-medium text-[#F1ECE0]/85 py-2 border-b border-white/5"
                >
                  {n.label}
                </Link>
              ))}
              <Link
                to="/book"
                onClick={() => setOpen(false)}
                className="mt-4 text-center text-[11px] uppercase tracking-[0.22em] font-semibold px-5 py-3 bg-[#D9FF3C] text-[#142645]"
              >
                Book consultation
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
